import { useCallback, useEffect, useState } from 'react';

import { getTasks } from 'services/dashboard.service';

import { AcaoEcoa, CicloDoSemestre, janelaDoCiclo } from './acoes-do-semestre';

const SEM_ACOES: AcaoEcoa[] = [];

interface Resultado {
  /** Chave da leitura que produziu `acoes` (usuário + ciclo + janela). */
  leitura?: string;
  acoes: AcaoEcoa[];
}

/**
 * Ações ECOA do integrante no ciclo inteiro: uma leitura de `find/entregas_ecoa` com a janela do ciclo e sem
 * `step` (ECN-01). O efeito depende só de primitivos (AD-023); a resposta de um ciclo anterior que chega tarde é
 * descartada e a lista exposta é sempre a da leitura do ciclo atual (ECN-03). Erro não apaga a lista já
 * carregada do mesmo ciclo; `recarregar` refaz a leitura (ECN-17).
 */
export function useAcoesDoSemestre(chave: string | undefined, ciclo: CicloDoSemestre | undefined) {
  const janela = ciclo ? janelaDoCiclo(ciclo) : undefined;
  const inicio = janela?.start;
  const fim = janela?.end;
  const leitura = chave && ciclo ? `${chave}|${ciclo._id}|${inicio}|${fim}` : undefined;

  const [resultado, setResultado] = useState<Resultado>({ acoes: SEM_ACOES });
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(false);
  const [versao, setVersao] = useState(0);

  useEffect(() => {
    if (!leitura) {
      setCarregando(false);
      return;
    }

    let descartada = false;
    setCarregando(true);
    setErro(false);

    getTasks(chave, inicio, fim, true)
      .then(acoes => {
        if (!descartada) setResultado({ leitura, acoes });
      })
      .catch(() => {
        if (!descartada) setErro(true);
      })
      .finally(() => {
        if (!descartada) setCarregando(false);
      });

    return () => {
      descartada = true;
    };
  }, [leitura, chave, inicio, fim, versao]);

  const recarregar = useCallback(() => setVersao(atual => atual + 1), []);

  return {
    acoes: resultado.leitura === leitura ? resultado.acoes : SEM_ACOES,
    carregando,
    erro,
    recarregar,
  };
}
